import React, { useState, useEffect } from 'react';

// (Sep 2026) System Authority admin screen — Create Region plus the GA
// envelope increase-request approval inbox (screen d). Shown only when
// isSA, computed server-side in landingService.js and passed down as a
// prop.
//
// SPLIT 8 SEP 2026 from the original combined AuthorityTab.jsx: see
// RegionalAuthorityTab.jsx's header comment for the full rationale. In
// short, rendering the SA's own actions on the same page as the RA's
// request form made it unclear whose envelope was being acted on when one
// account (nmnajida@gmail.com, for testing) holds both roles at once.
// Splitting into two tabs — one per authority tier — removes that
// ambiguity, even though a real RA and the real SA are almost never the
// same person.

// (19 Sep 2026) The four per-calendar abuse limits (Reference Appendix
// Section 9). Keys match the camelCase fields returned by, and accepted by,
// GET /api/authority/calendar-limits and PATCH
// /api/authority/calendars/:calendarId/limits (authorityRoutes.js).
const LIMIT_FIELD_DEFS = [
  { key: 'maxEventsPerPeriod', label: 'Events per period' },
  { key: 'eventPeriodDays', label: 'Period (days)' },
  { key: 'maxAdvanceDays', label: 'Advance horizon (days)' },
  { key: 'maxMembers', label: 'Max members' },
];

// (27 Oct 2026, decision 150) "1 Nov 2026" from a YYYY-MM-DD date, read in UTC so
// the day never shifts.
const regionDate = (ymd) => {
  if (!ymd) return null;
  const d = new Date(`${String(ymd).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const SystemAuthorityTab = ({ apiBaseUrl, authToken, isSA, onStatusMessage }) => {
  // --- Regions list (SA only, read-only; 27 Oct 2026, decision 150) ---
  // Until now the SA could create a region but nothing showed which regions
  // exist. One card per region from GET /api/authority/regions
  // (authorityRoutes.js): its Regional Authority, any RA stand-ins, the Group
  // Admin count against the envelope, and its calendar count.
  const [regions, setRegions] = useState([]);
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);

  const refetchRegions = () => {
    setIsLoadingRegions(true);
    fetch(`${apiBaseUrl}/api/authority/regions`, {
      headers: { 'Authorization': `Bearer ${authToken}` }
    })
      .then(async (res) => {
        if (res.ok) return res.json();
        const body = await res.json().catch(() => ({}));
        onStatusMessage(body.error || 'Failed to load regions', 'error');
        return [];
      })
      .then((data) => setRegions(Array.isArray(data) ? data : []))
      .catch(() => {
        onStatusMessage('Failed to load regions', 'error');
        setRegions([]);
      })
      .finally(() => setIsLoadingRegions(false));
  };

  useEffect(() => {
    if (isSA) refetchRegions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSA, apiBaseUrl, authToken]);

  const personLabel = (p) => {
    if (!p) return 'none';
    if (p.preferredName && p.email) return `${p.preferredName} (${p.email})`;
    return p.preferredName || p.email || 'unknown account';
  };

  // --- Create Region (SA only) ---
  const [regionName, setRegionName] = useState('');
  const [raEmail, setRaEmail] = useState('');
  const [isCreatingRegion, setIsCreatingRegion] = useState(false);

  const handleCreateRegion = async () => {
    if (!regionName.trim() || !raEmail.trim()) {
      onStatusMessage('Please enter a region name and the RA\'s email', 'error');
      return;
    }
    setIsCreatingRegion(true);
    try {
      const lookupRes = await fetch(`${apiBaseUrl}/api/users/lookup?email=${encodeURIComponent(raEmail.trim())}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const lookupData = await lookupRes.json();
      if (!lookupRes.ok) {
        onStatusMessage(lookupData.error || 'Failed to look up that account', 'error');
        return;
      }

      const res = await fetch(`${apiBaseUrl}/api/regions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: regionName.trim(), ra_user_id: lookupData.id })
      });
      const data = await res.json();
      if (res.ok) {
        onStatusMessage(data.message || 'Region created', 'success');
        setRegionName('');
        setRaEmail('');
        refetchRegions();
      } else {
        onStatusMessage(data.error || 'Failed to create region', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to create region', 'error');
    } finally {
      setIsCreatingRegion(false);
    }
  };

  // --- GA envelope increase requests: SA-approval-inbox (screen d, 8 Sep) ---
  // Pending requests across ALL regions — an SA oversees every RA's
  // envelope, so this isn't scoped to any one region.
  const [pendingRequests, setPendingRequests] = useState([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [resolvingRequestId, setResolvingRequestId] = useState(null);

  const refetchPendingRequests = () => {
    setIsLoadingRequests(true);
    fetch(`${apiBaseUrl}/api/authority/ga-cap-requests`, {
      headers: { 'Authorization': `Bearer ${authToken}` }
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setPendingRequests(data))
      .catch(() => setPendingRequests([]))
      .finally(() => setIsLoadingRequests(false));
  };

  useEffect(() => {
    if (isSA) refetchPendingRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSA, apiBaseUrl, authToken]);

  const handleResolveRequest = async (requestId, decision) => {
    setResolvingRequestId(requestId);
    try {
      const res = await fetch(`${apiBaseUrl}/api/authority/ga-cap-requests/${requestId}/resolve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ decision })
      });
      const data = await res.json();
      if (res.ok) {
        onStatusMessage(data.message || 'Request resolved', 'success');
        refetchPendingRequests();
      } else {
        onStatusMessage(data.error || 'Failed to resolve request', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to resolve request', 'error');
    } finally {
      setResolvingRequestId(null);
    }
  };

  // --- Per-calendar limits (SA only, 19 Sep 2026) ---
  // The server is the only place these are enforced; this screen just edits
  // them. Drafts are kept as strings per calendar so a half-typed value never
  // touches the saved one, and only the fields actually changed are sent.
  const [calendarLimits, setCalendarLimits] = useState([]);
  const [isLoadingLimits, setIsLoadingLimits] = useState(false);
  const [limitDrafts, setLimitDrafts] = useState({});
  const [savingLimitsId, setSavingLimitsId] = useState(null);

  const refetchCalendarLimits = () => {
    setIsLoadingLimits(true);
    fetch(`${apiBaseUrl}/api/authority/calendar-limits`, {
      headers: { 'Authorization': `Bearer ${authToken}` }
    })
      .then(async (res) => {
        if (res.ok) return res.json();
        const body = await res.json().catch(() => ({}));
        onStatusMessage(body.error || 'Failed to load calendar limits', 'error');
        return [];
      })
      .then((data) => {
        setCalendarLimits(Array.isArray(data) ? data : []);
        setLimitDrafts({});
      })
      .catch(() => {
        onStatusMessage('Failed to load calendar limits', 'error');
        setCalendarLimits([]);
      })
      .finally(() => setIsLoadingLimits(false));
  };

  useEffect(() => {
    if (isSA) refetchCalendarLimits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSA, apiBaseUrl, authToken]);

  const draftValue = (cal, key) => {
    const d = limitDrafts[cal.id];
    return d && d[key] !== undefined ? d[key] : String(cal[key]);
  };

  const isLimitsDirty = (cal) =>
    LIMIT_FIELD_DEFS.some((f) => draftValue(cal, f.key) !== String(cal[f.key]));

  const handleLimitChange = (cal, key, value) => {
    setLimitDrafts((prev) => ({ ...prev, [cal.id]: { ...(prev[cal.id] || {}), [key]: value } }));
  };

  const handleSaveLimits = async (cal) => {
    const body = {};
    for (const f of LIMIT_FIELD_DEFS) {
      const draft = draftValue(cal, f.key);
      if (draft === String(cal[f.key])) continue; // unchanged - not sent
      const n = Number(draft);
      if (draft.trim() === '' || !Number.isInteger(n) || n < 1) {
        onStatusMessage(`${f.label} must be a whole number of at least 1`, 'error');
        return;
      }
      body[f.key] = n;
    }
    if (Object.keys(body).length === 0) return;

    setSavingLimitsId(cal.id);
    try {
      const res = await fetch(`${apiBaseUrl}/api/authority/calendars/${cal.id}/limits`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setCalendarLimits((prev) => prev.map((c) => (c.id === cal.id ? { ...c, ...data.limits } : c)));
        setLimitDrafts((prev) => {
          const next = { ...prev };
          delete next[cal.id];
          return next;
        });
        onStatusMessage(data.message || 'Limits saved', 'success');
      } else {
        onStatusMessage(data.error || 'Failed to save limits', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to save limits', 'error');
    } finally {
      setSavingLimitsId(null);
    }
  };

  // (8 Sep 2026) One row's before→after: shows "—" when there's nothing to
  // compare (no current value set yet, or this field wasn't requested).
  const renderDelta = (label, current, requested) => {
    if (requested == null) return null;
    return (
      <span className="block">
        {label}: {current != null ? current : '—'} → <span className="font-medium text-gray-900">{requested}</span>
      </span>
    );
  };

  if (!isSA) {
    return <p className="text-sm text-gray-500">You don't hold System Authority.</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-3">Regions</h3>
        <div className="border border-gray-200 rounded-lg p-3">
          {isLoadingRegions ? (
            <p className="text-xs text-gray-400">Loading…</p>
          ) : regions.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No regions yet.</p>
          ) : (
            <ul className="space-y-3">
              {regions.map((r) => (
                <li key={r.id} className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-medium text-gray-800 min-w-0 break-words">{r.name}</p>
                    <span className="shrink-0 text-xs text-gray-500">
                      {r.calendarCount} calendar{r.calendarCount === 1 ? '' : 's'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 break-words">
                    Regional Authority: {personLabel(r.ra)}
                  </p>
                  <p className="text-xs text-gray-600">
                    Group Admins: {r.gaCount}
                    {r.maxGas != null ? ` of ${r.maxGas}` : ' (no limit set)'}
                    {r.defaultGaCap != null && ` · cap ceiling ${r.defaultGaCap}`}
                  </p>
                  {r.standins.length === 0 ? (
                    <p className="text-xs text-gray-600">RA stand-ins: none</p>
                  ) : (
                    <div className="text-xs text-gray-600">
                      <p>RA stand-ins:</p>
                      <ul className="ml-3">
                        {r.standins.map((s) => {
                          const from = regionDate(s.startDate);
                          const to = regionDate(s.endDate);
                          return (
                            <li key={s.userId} className="break-words">
                              {personLabel(s)}
                              {(from || to) && `, ${from || 'open'} to ${to || 'open'}`}
                              {s.holdsAuthority && <span className="font-medium text-gray-800"> · holds authority now</span>}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3">Create Region</h3>
        <div className="border border-gray-200 rounded-lg p-3 space-y-2">
          <input
            type="text"
            value={regionName}
            onChange={(e) => setRegionName(e.target.value)}
            placeholder="Region name"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <input
            type="email"
            autoComplete="off"
            value={raEmail}
            onChange={(e) => setRaEmail(e.target.value)}
            placeholder="Regional Authority's email"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <button
            onClick={handleCreateRegion}
            disabled={isCreatingRegion}
            className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
          >
            {isCreatingRegion ? 'Creating…' : 'Create Region'}
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3">GA Envelope Increase Requests</h3>
        <div className="border border-gray-200 rounded-lg p-3 space-y-2">
          {isLoadingRequests ? (
            <p className="text-xs text-gray-400">Loading…</p>
          ) : pendingRequests.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No pending requests.</p>
          ) : (
            <ul className="space-y-3">
              {pendingRequests.map((r) => (
                <li key={r.id} className="border-b border-gray-100 pb-2 last:border-b-0 last:pb-0">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">{r.raPreferredName || r.raEmail}</span>
                    {' — '}{r.regionName}
                  </p>
                  <p className="text-xs text-gray-500 mb-2">
                    {renderDelta('Max GAs', r.currentMaxGas, r.requestedMaxGas)}
                    {renderDelta('Default cap', r.currentDefaultGaCap, r.requestedDefaultGaCap)}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleResolveRequest(r.id, 'approve')}
                      disabled={resolvingRequestId === r.id}
                      className="flex-1 px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleResolveRequest(r.id, 'deny')}
                      disabled={resolvingRequestId === r.id}
                      className="flex-1 px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                    >
                      Deny
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-1">Calendar Limits</h3>
        <p className="text-xs text-gray-500 mb-3">
          Abuse limits, enforced on the server for every calendar and changeable only here. A calendar
          may hold at most <em>Events per period</em> events in any <em>Period</em>-day window (counted by
          event date), none further ahead than the <em>Advance horizon</em>, and at most <em>Max members</em> member
          rows (pending sign-ins included). Lowering a limit never removes anything that already exists;
          it only affects what can be added or moved afterwards.
        </p>
        <div className="border border-gray-200 rounded-lg p-3 space-y-2">
          {isLoadingLimits ? (
            <p className="text-xs text-gray-400">Loading…</p>
          ) : calendarLimits.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No calendars found.</p>
          ) : (
            <ul className="space-y-4">
              {calendarLimits.map((c) => (
                <li key={c.id} className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate">
                        {c.name}
                        {!c.active && <span className="ml-2 text-xs font-normal text-gray-400">(inactive)</span>}
                      </p>
                      {c.ownerEmail && <p className="text-xs text-gray-400 truncate">{c.ownerEmail}</p>}
                    </div>
                    <span
                      className="shrink-0 text-xs text-gray-500"
                      title="Includes members who have not signed in yet"
                    >
                      {c.memberCount} member{c.memberCount === 1 ? '' : 's'} now
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {LIMIT_FIELD_DEFS.map((f) => (
                      <label key={f.key} className="block">
                        <span className="block text-xs text-gray-500 mb-0.5">{f.label}</span>
                        <input
                          type="number"
                          inputMode="numeric"
                          min="1"
                          step="1"
                          autoComplete="off"
                          value={draftValue(c, f.key)}
                          onChange={(e) => handleLimitChange(c, f.key, e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                        />
                      </label>
                    ))}
                  </div>
                  <button
                    onClick={() => handleSaveLimits(c)}
                    disabled={!isLimitsDirty(c) || savingLimitsId === c.id}
                    className="mt-2 w-full px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                  >
                    {savingLimitsId === c.id ? 'Saving…' : 'Save limits'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemAuthorityTab;
